import Icon from '@ant-design/icons';
import { Button } from 'antd';
import { Template } from 'devextreme-react/core/template';
import DataGrid, { Column, FilterRow, GroupPanel, Grouping, HeaderFilter, Pager, Paging } from 'devextreme-react/data-grid';
import { dxToolbarOptions } from 'devextreme/ui/toolbar';
import { computed } from 'mobx';
import { observer } from 'mobx-react';
import React, { useRef } from 'react';

import { ReactComponent as filter } from '@assets/icons/filter.svg';

import AdaptiveCard from '@components/fw/AdaptiveElements/AdaptiveCard';
import CatalogDxCellReport from '@components/fw/DxCellTemplates/CatalogDxCellReport';
import NumberFilterModal from '@components/fw/NumbersFilter/NumberFilterModal';
import { INDEX_COLUMN, getFilterDatasource } from '@components/routed/DispatcherReport/parts/DispetcherReportParts';
import { UserOperationInfoForm } from '@components/routed/UserOperationInfoPage/ui/form';

import { hatchingRow } from '@stores/dispatcherStore/DispatcherData';
import { userOperationInfoStore } from '@stores/userOperationInfoStore';

import { toolbarPreparing } from './lib/toolbar-preparing';
import { UserOperationInfoTableProps } from './types/table-props';

export const UserOperationInfoTable = observer(({ dataSource }: UserOperationInfoTableProps) => {
  const dataGridRef = useRef<DataGrid>(null);

  const selectedIds = computed(() => userOperationInfoStore.selectedIds).get();

  const onSelectionChange = (e: any) => {
    userOperationInfoStore.setSelectedIds(e.selectedRowsData.map((row: any) => row.useroperationinfoid));
  };

  const onRestore = () => {
    dataGridRef.current.instance.clearFilter();
    dataGridRef.current.instance.clearSorting();
    dataGridRef.current.instance.clearSelection();
    dataGridRef.current.instance.clearGrouping();
    userOperationInfoStore.filteredWagons = [];
    onRefresh();
  };

  const onRefresh = () => dataSource.reload();

  const getFilter = (fieldName) => {
    return {
      allowSearch: true,
      dataSource: getFilterDatasource(fieldName.toLowerCase()),
      groupInterval: undefined,
      height: undefined,
      searchMode: undefined,
      width: undefined,
    };
  };

  const onToolbarPreparing = (e: { toolbarOptions?: dxToolbarOptions }) => toolbarPreparing(onRestore, onRefresh, e);

  const onFillClick = () => {
    userOperationInfoStore.setShowForm(true);
  };

  const renderNumberFilter = () => {
    const hasFilter = !!userOperationInfoStore.filteredWagons.length;
    return (
      <Button
        style={{ marginRight: 14 }}
        title={'Фильтр по списку номеров'}
        type={hasFilter ? 'primary' : 'text'}
        icon={<Icon style={{ color: hasFilter ? null : '#4977e9' }} component={filter} />}
        onClick={() => (userOperationInfoStore.numbersFilterModalVisible = true)}
      />
    );
  };

  return (
    <AdaptiveCard>
      <UserOperationInfoForm onChange={() => dataSource.reload()} />
      <DataGrid
        ref={dataGridRef}
        onRowPrepared={hatchingRow}
        allowColumnReordering={true}
        dataSource={dataSource}
        height={TABLE_HEIGHT}
        onSelectionChanged={onSelectionChange}
        onToolbarPreparing={onToolbarPreparing}
        selection={{ mode: 'multiple', showCheckBoxesMode: 'always' }}
        allowColumnResizing={true}
        showBorders={true}
        columnResizingMode={'widget'}
        columnAutoWidth={true}
        showColumnLines={true}
        columnMinWidth={30}
        showRowLines={true}
        rowAlternationEnabled={true}
        remoteOperations={true}>
        <Template name={'numberFilter'} render={renderNumberFilter} />
        <Template
          name={'fill'}
          render={() => (
            <Button disabled={!selectedIds.length} type={'default'} onClick={onFillClick}>
              Заполнить
            </Button>
          )}
        />
        <FilterRow visible={true} />
        <HeaderFilter visible={true} />
        <GroupPanel visible={true} />
        <Grouping autoExpandAll={false} />
        <Paging enabled={true} defaultPageSize={25} />
        <Pager
          allowedPageSizes={[25, 50, 100]}
          showPageSizeSelector={true}
          showNavigationButtons={true}
          showInfo={true}
          infoText={'Всего: {2}'}
        />
        {INDEX_COLUMN}

        <Column allowFixing={true} dataField={'wagonnumber'} caption={'Номер вагона'} dataType={'number'} />
        <Column allowFixing={true} dataField={'loadstationname'} caption={'Станция погрузки'} dataType={'string'} />
        <Column allowFixing={true} dataField={'deststationname'} caption={'Станция назначения'} dataType={'string'} />
        <Column allowFixing={true} dataField={'dislstationname'} caption={'Станция дислокации'} dataType={'string'} />
        <Column allowFixing={true} dataField={'operationdate'} caption={'Дата операции'} dataType={'datetime'} />
        <Column
          allowFixing={true}
          dataField={'wagoncatalogs'}
          caption={'Подразделения'}
          cellRender={CatalogDxCellReport}
          groupCellRender={CatalogDxCellReport}
          headerFilter={getFilter('wagoncatalogs')}
        />
        <Column allowFixing={true} dataField={'filldate'} caption={'Дата заполнения'} dataType={'datetime'} />
      </DataGrid>
      <NumberFilterModal store={userOperationInfoStore} primaryKey={'wagonnumber'} onChange={onRefresh} />
    </AdaptiveCard>
  );
});

const TABLE_HEIGHT = 'calc(100vh - var(--padding))';
